import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronRight, Loader2 } from "lucide-react";
import { useBilling } from "../context/BillingContext";
import PackPreviewSheet from "./PackPreviewSheet";

/**
 * Remotely pack picker — replaces the old "Start a room" name-input page.
 * Mirrors the Play Together pick-a-pack layout, with a name field at top so
 * we can create the room in one step.
 */

const PACK_CATALOG = [
  {
    id: "spicy-mode",      name: "Spicy Vibes",     icon: "🌶️", img: "/assets/home/pack-spicy.png",
    tag: "HOT",            tagBg: "rgba(255, 46, 154, 0.15)", tagBorder: "#FF2E9A",  tagText: "#FF2E9A",
    accent: "#FF2E9A",     description: "Hot questions for bold nights.",            spice: 3, spiceColor: "#FF2E9A",
    tier: "premium",
  },
  {
    id: "deep-talk",       name: "Deep Talks",      icon: "💜", img: "/assets/home/pack-deep.png",
    tag: "DEEP",           tagBg: "rgba(155, 107, 255, 0.18)", tagBorder: "#9B6BFF", tagText: "#C9B7FF",
    accent: "#9B6BFF",     description: "Get vulnerable. Build connection.",          spice: 3, spiceColor: "#9B6BFF",
    tier: "premium",
  },
  {
    id: "we-listen",       name: "Most Likely To",  icon: "🤪", img: "/assets/home/pack-fun.png",
    tag: "FUN",            tagBg: "rgba(255, 195, 0, 0.18)",   tagBorder: "#FFC300", tagText: "#FFD45E",
    accent: "#FFB547",     description: "Chaos activated. Who said that? 👀",         spice: 3, spiceColor: "#FFD45E",
    tier: "free",
  },
  {
    id: "date-night",      name: "Date Night",      icon: "🍷", img: "/assets/home/pack-romantic.png",
    tag: "ROMANTIC",       tagBg: "rgba(255, 107, 71, 0.18)",  tagBorder: "#FF6B47", tagText: "#FF9F7A",
    accent: "#FF6B47",     description: "Cute, flirty & fun. Perfect for you two.",   spice: 3, spiceColor: "#FF6B47",
    tier: "free",
  },
  {
    id: "lowkey-toxic",    name: "Red Flag Radar",  icon: "👀", img: "/assets/home/pack-redflag.png",
    tag: "BOLD",           tagBg: "rgba(255, 61, 90, 0.18)",   tagBorder: "#FF3D5A", tagText: "#FF6B85",
    accent: "#FF3D5A",     description: "Expose gently. Find out the truth.",         spice: 3, spiceColor: "#FF3D5A",
    tier: "premium",
  },
  {
    id: "after-dark",      name: "Drunk Questions", icon: "🍸", img: "/assets/home/pack-night.png",
    tag: "WILD",           tagBg: "rgba(61, 214, 140, 0.18)",  tagBorder: "#3DD68C", tagText: "#7DE6A9",
    accent: "#3DD68C",     description: "No filters. No limits. Just chaos.",         spice: 3, spiceColor: "#3DD68C",
    tier: "premium",
  },
];

function FloatingParticles({ density = 12, color = "#9B6BFF" }) {
  const particles = Array.from({ length: density }, (_, i) => ({
    left: ((i * 11 + 5) % 100),
    size: 1.5 + (i % 4) * 0.7,
    delay: (i * 0.6) % 8,
    duration: 9 + (i % 5),
    opacity: 0.25 + ((i % 6) * 0.07),
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {particles.map((p, i) => (
        <span
          key={i}
          style={{
            position: "absolute", top: 0,
            left: `${p.left}%`,
            width: p.size, height: p.size,
            borderRadius: "50%",
            background: color, boxShadow: `0 0 ${p.size * 3}px ${color}`,
            opacity: p.opacity,
            animation: `vcFloatUp ${p.duration}s linear infinite`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes vcFloatUp {
          0%   { transform: translateY(110vh); opacity: 0; }
          15%  { opacity: 0.7; }
          85%  { opacity: 0.7; }
          100% { transform: translateY(-12vh); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default function PlayRemotelyPickPack({ onBack, onContinue, loading }) {
  const { isPremium, isPackUnlocked, openPaywall } = useBilling();
  const [packId, setPackId] = useState(null);
  const [previewing, setPreviewing] = useState(null);

  const handlePick = (id) => {
    if (loading || packId) return; // debounce: ignore taps once a pack is locked in
    const meta = PACK_CATALOG.find((p) => p.id === id);
    if (meta?.tier === "premium" && !isPremium && !isPackUnlocked(id)) {
      setPreviewing(meta);
      return;
    }
    setPackId(id);
    // Brief visual confirmation then advance
    setTimeout(() => onContinue?.({ packId: id }), 180);
  };

  return (
    <div className="vc-bg min-h-screen relative overflow-hidden" data-testid="remotely-pickpack-screen">
      <FloatingParticles density={14} color="#9B6BFF" />
      <main className="relative z-[5] max-w-md mx-auto px-5 pt-5 pb-12 min-h-screen flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-3 relative z-[2]">
          <button
            onClick={onBack}
            aria-label="Back"
            data-testid="remotely-pickpack-back"
            className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95"
            style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-10" aria-hidden />
        </div>

        {/* Title */}
        <div className="text-center mb-5 mt-1 relative z-[2]">
          <h1
            className="text-white font-extrabold tracking-tight inline-flex items-center gap-2"
            style={{ fontFamily: "'Manrope', sans-serif", fontSize: "clamp(1.7rem, 6.4vw, 2.1rem)", letterSpacing: "-0.025em" }}
            data-testid="remotely-pickpack-title"
          >
            Pick your pack
            <motion.span
              animate={{ rotate: [0, 8, -8, 0], scale: [1, 1.12, 1] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              style={{ filter: "drop-shadow(0 0 10px rgba(155, 107, 255, 0.85))" }}
            >
              ✨
            </motion.span>
          </h1>
          <p className="text-[#B8B8CC] mt-2" style={{ fontSize: "13.5px" }}>
            Tap a pack to start. The vibe begins instantly.
          </p>
        </div>

        {/* Pack cards */}
        <div className="space-y-3 mb-5 relative z-[2]" data-testid="remotely-pickpack-list">
          {PACK_CATALOG.map((p, idx) => {
            const sel = packId === p.id;
            return (
              <motion.button
                key={p.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.05 + idx * 0.05, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => handlePick(p.id)}
                disabled={loading}
                data-testid={`remotely-pickpack-card-${p.id}`}
                whileTap={{ scale: 0.985 }}
                className="w-full rounded-2xl p-3.5 flex items-center gap-3 text-left transition relative overflow-hidden disabled:opacity-60"
                style={{
                  background: `linear-gradient(180deg, ${p.accent}1f 0%, rgba(10, 6, 10, 0.78) 100%)`,
                  border: `1.5px solid ${sel ? p.accent : `${p.accent}55`}`,
                  boxShadow: sel
                    ? `0 0 26px ${p.accent}b0, inset 0 0 22px ${p.accent}26, inset 0 1px 0 rgba(255,255,255,0.08)`
                    : `0 0 12px ${p.accent}3a, inset 0 0 18px ${p.accent}14, inset 0 1px 0 rgba(255,255,255,0.06)`,
                  backdropFilter: "blur(14px)",
                  WebkitBackdropFilter: "blur(14px)",
                }}
              >
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: -16, top: -16, width: 120, height: 120,
                    background: `radial-gradient(circle, ${p.accent}40 0%, transparent 70%)`,
                    filter: "blur(18px)",
                  }}
                  aria-hidden
                />
                <div
                  className="relative flex-shrink-0 w-[68px] h-[68px] rounded-2xl overflow-hidden flex items-center justify-center"
                  style={{
                    background: `radial-gradient(circle, ${p.accent}33 0%, rgba(8, 4, 12, 0.85) 75%)`,
                    border: `1px solid ${p.accent}66`,
                    boxShadow: `inset 0 0 14px ${p.accent}30, 0 0 14px ${p.accent}80`,
                  }}
                >
                  <img
                    src={p.img}
                    alt=""
                    className="w-[60px] h-[60px] object-contain"
                    style={{ filter: `drop-shadow(0 0 10px ${p.accent})` }}
                  />
                </div>
                <div className="flex-1 min-w-0 relative">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-white text-[15px] font-extrabold leading-tight" style={{ letterSpacing: "-0.01em" }}>
                      {p.name}
                    </h3>
                    <span
                      className="text-[9.5px] font-black tracking-[0.16em] px-2 py-[3px] rounded-md"
                      style={{
                        background: p.tagBg,
                        color: p.tagText,
                        border: `1px solid ${p.tagBorder}`,
                        boxShadow: `0 0 10px ${p.tagBorder}66, inset 0 0 6px ${p.tagBorder}1f`,
                      }}
                    >
                      {p.tag}
                    </span>
                    {/* Free / Premium tier badge */}
                    <span
                      className="inline-flex items-center gap-1 text-[9px] font-black tracking-[0.1em] px-2 py-[3px] rounded-full uppercase"
                      style={
                        p.tier === "premium"
                          ? {
                              background: "linear-gradient(135deg, #FF2E9A, #FF6B47)",
                              color: "#FFFFFF",
                              border: "1px solid rgba(255, 255, 255, 0.35)",
                              boxShadow: "0 0 12px rgba(255, 46, 154, 0.7)",
                            }
                          : {
                              background: "rgba(61, 214, 140, 0.16)",
                              color: "#7DE6A9",
                              border: "1px solid rgba(61, 214, 140, 0.6)",
                              boxShadow: "0 0 8px rgba(61, 214, 140, 0.4)",
                            }
                      }
                      data-testid={`remotely-pickpack-tier-${p.id}`}
                    >
                      {p.tier === "premium" ? "💎 Premium" : "✓ Free"}
                    </span>
                  </div>
                  <p className="text-[#B0AABB] text-[11.5px] leading-snug">{p.description}</p>
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0 relative">
                  <div className="flex gap-1">
                    {Array(p.spice).fill(0).map((_, i) => (
                      <span
                        key={i}
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: p.spiceColor, boxShadow: `0 0 6px ${p.spiceColor}` }}
                      />
                    ))}
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/55" />
                </div>
              </motion.button>
            );
          })}
        </div>

        <div className="flex-1" />

        {/* Loading footer (only while creating room) */}
        {loading && (
          <div className="relative z-[2] flex items-center justify-center gap-2 text-white/75 py-3" data-testid="remotely-pickpack-loading">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-[14px] font-bold">Creating room…</span>
          </div>
        )}
      </main>

      <PackPreviewSheet
        pack={previewing}
        onClose={() => setPreviewing(null)}
        onUnlock={() => {
          const id = previewing?.id;
          setPreviewing(null);
          openPaywall("pack_locked", id);
        }}
      />
    </div>
  );
}
